package com.fantasytenis.web.components.bracket

import com.fantasytenis.shared.domain.bracket.entity.BracketMatchEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketRoundEntity
import com.fantasytenis.shared.domain.bracket.entity.DrawPositionEntity
import com.fantasytenis.shared.domain.bracket.entity.RoundName

/**
 * Display information for a player in a bracket match.
 * This can come from either:
 * 1. Actual draw data (for seeded positions in R128/R64)
 * 2. User's picks (for later rounds where winner depends on earlier picks)
 * 3. Potential players (when no pick made yet, shows who could be here)
 */
data class DisplayPlayer(
    val name: String,
    val seed: Int?,
    val country: String?,
    val playerId: String?,
    val isFromPick: Boolean,
    val sourceMatchId: String? = null, // The match this player advanced from (if from pick)
    val potentialPlayers: List<String>? = null // Players who could advance here (when no pick made)
)

/**
 * Calculator for determining what players to display in bracket matches
 * based on actual draw data and user's picks.
 *
 * In a bracket prediction challenge:
 * - R128/R64 positions have actual players from the draw
 * - R32+ positions depend on who the user picked to win earlier rounds
 *
 * This calculator traces the pick chain to show the user's predicted bracket.
 */
object PickAdvancementCalculator {

    /**
     * Get the effective display player for a position in a match.
     *
     * @param position The draw position entity
     * @param matchRound The round this match is in
     * @param picks Map of positionId -> winnerId (user's picks)
     * @param allRounds All bracket rounds for tracing feeder matches
     * @return DisplayPlayer with appropriate info based on picks
     */
    fun getDisplayPlayer(
        position: DrawPositionEntity,
        matchRound: RoundName,
        picks: Map<String, String>,
        allRounds: List<BracketRoundEntity>
    ): DisplayPlayer {

        // Case 1: Position already has player data from the draw
        // Check both playerName and playerId to ensure we have actual player data
        // (Backend sends empty strings instead of null for missing data)
        if (!position.playerName.isNullOrBlank() && !position.playerId.isNullOrBlank()) {
            return DisplayPlayer(
                name = position.displayName,
                seed = position.seed,
                country = position.playerCountry,
                playerId = position.playerId,
                isFromPick = false
            )
        }

        // Case 2: Find the feeder match and check if user made a pick
        val feederMatch = findFeederMatch(position, matchRound, allRounds)
        if (feederMatch != null) {
            val pickedWinnerId = picks[feederMatch.player1.id]
            if (pickedWinnerId != null) {
                // User picked a winner - determine which player won
                val winner = when (pickedWinnerId) {
                    feederMatch.player1.playerId -> feederMatch.player1
                    feederMatch.player2.playerId -> feederMatch.player2
                    else -> null
                }

                if (winner != null) {
                    // Recursively get display info (in case the winner was also from a pick)
                    val winnerDisplay = getDisplayPlayer(
                        position = winner,
                        matchRound = feederMatch.round,
                        picks = picks,
                        allRounds = allRounds
                    )

                    return DisplayPlayer(
                        name = winnerDisplay.name,
                        seed = winnerDisplay.seed,
                        country = winnerDisplay.country,
                        playerId = winner.playerId,
                        isFromPick = true,
                        sourceMatchId = feederMatch.id
                    )
                }
            } else {
                // Case 2b: Found feeder match but no pick made yet - show potential players
                // Recursively get display info for both potential players
                val p1Display = getDisplayPlayer(feederMatch.player1, feederMatch.round, picks, allRounds)
                val p2Display = getDisplayPlayer(feederMatch.player2, feederMatch.round, picks, allRounds)

                // Collect potential player names (filter out TBD to avoid nested "TBD")
                val potentialNames = listOf(p1Display.name, p2Display.name)
                    .filter { it != "TBD" }

                return DisplayPlayer(
                    name = "TBD",
                    seed = null,
                    country = null,
                    playerId = null,
                    isFromPick = false,
                    potentialPlayers = potentialNames.ifEmpty { null }
                )
            }
        }

        // Case 3: No player data and no feeder match found
        return DisplayPlayer(
            name = "TBD",
            seed = null,
            country = null,
            playerId = null,
            isFromPick = false
        )
    }

    /**
     * Get both display players for a match.
     *
     * @param match The bracket match
     * @param picks User's picks
     * @param allRounds All bracket rounds
     * @return Pair of (player1Display, player2Display)
     */
    fun getMatchDisplayPlayers(
        match: BracketMatchEntity,
        picks: Map<String, String>,
        allRounds: List<BracketRoundEntity>
    ): Pair<DisplayPlayer, DisplayPlayer> {
        val player1Display = getDisplayPlayer(
            position = match.player1,
            matchRound = match.round,
            picks = picks,
            allRounds = allRounds
        )
        val player2Display = getDisplayPlayer(
            position = match.player2,
            matchRound = match.round,
            picks = picks,
            allRounds = allRounds
        )
        return Pair(player1Display, player2Display)
    }

    /**
     * Find the feeder match that determines who advances to this position.
     *
     * In a bracket, each match in round N is fed by 2 matches from round N-1:
     * - R64 Match 0's player1 slot ← winner of R128 Match 0
     * - R64 Match 0's player2 slot ← winner of R128 Match 1
     * - R64 Match 1's player1 slot ← winner of R128 Match 2
     * - R64 Match 1's player2 slot ← winner of R128 Match 3
     * - etc.
     *
     * IMPORTANT: Position numbers are GLOBAL draw positions (not round-relative).
     * R128 uses positions 127-254, R64 uses 63-126, R32 uses 31-62, etc.
     * So we must find the match by position ID, not by position number.
     */
    private fun findFeederMatch(
        position: DrawPositionEntity,
        currentRound: RoundName,
        allRounds: List<BracketRoundEntity>
    ): BracketMatchEntity? {
        val previousRound = getPreviousRound(currentRound) ?: return null
        val previousRoundData = allRounds.find { it.name == previousRound } ?: return null
        val currentRoundData = allRounds.find { it.name == currentRound } ?: return null

        // Find which match in the current round contains this position
        val currentMatchIndex = currentRoundData.matches.indexOfFirst { match ->
            match.player1.id == position.id || match.player2.id == position.id
        }
        if (currentMatchIndex == -1) return null

        // Determine if this position is player1 or player2 in the current match
        val currentMatch = currentRoundData.matches[currentMatchIndex]
        val isPlayer1 = currentMatch.player1.id == position.id

        // Each match in the current round is fed by 2 matches from the previous round
        // player1 slot comes from match at index (currentMatchIndex * 2)
        // player2 slot comes from match at index (currentMatchIndex * 2 + 1)
        val feederMatchIndex = currentMatchIndex * 2 + (if (isPlayer1) 0 else 1)

        val feederMatch = previousRoundData.matches.getOrNull(feederMatchIndex)

        // Debug logging for R64
        if (currentRound == RoundName.R64) {
            console.log("findFeederMatch: R64 matchIdx=$currentMatchIndex, isPlayer1=$isPlayer1 -> feederIdx=$feederMatchIndex, feeder p1=${feederMatch?.player1?.playerName}, p2=${feederMatch?.player2?.playerName}")
        }

        return feederMatch
    }

    /**
     * Get the previous round in the tournament progression.
     */
    private fun getPreviousRound(round: RoundName): RoundName? {
        return when (round) {
            RoundName.R128 -> null // No previous round
            RoundName.R64 -> RoundName.R128
            RoundName.R32 -> RoundName.R64
            RoundName.R16 -> RoundName.R32
            RoundName.QF -> RoundName.R16
            RoundName.SF -> RoundName.QF
            RoundName.F -> RoundName.SF
        }
    }

    /**
     * Check if a player can be selected in a match based on picks.
     * A player can only be selected if they have either:
     * 1. Real player data (position.playerId != null)
     * 2. Been picked to advance from earlier rounds
     */
    fun isPlayerSelectable(
        position: DrawPositionEntity,
        matchRound: RoundName,
        picks: Map<String, String>,
        allRounds: List<BracketRoundEntity>
    ): Boolean {
        val displayPlayer = getDisplayPlayer(position, matchRound, picks, allRounds)
        return displayPlayer.playerId != null
    }

    /**
     * Get all players the user has picked to reach a specific round.
     * Useful for validation and UI highlighting.
     */
    fun getPickedPlayersForRound(
        round: RoundName,
        picks: Map<String, String>,
        allRounds: List<BracketRoundEntity>
    ): List<DisplayPlayer> {
        val roundData = allRounds.find { it.name == round } ?: return emptyList()
        val players = mutableListOf<DisplayPlayer>()

        roundData.matches.forEach { match ->
            val (p1, p2) = getMatchDisplayPlayers(match, picks, allRounds)
            if (p1.playerId != null) players.add(p1)
            if (p2.playerId != null) players.add(p2)
        }

        return players
    }
}
